import { useMemo } from 'react'
import { useConfig } from 'hooks'
import { ZeroAddress } from 'ethers'

import { useVaultsCountQuery } from 'graphql/subgraph/vaults'

import type { TabsProps } from 'sw-components'

import { filterOptions, filterTypes } from './data'


type Input = {
  filter: VaultsPage.SortFormParams['filter']
  blacklistTotal: number
}

type Output = {
  total: number
  isFetching: boolean
  filterTabs: TabsProps['tabsList']
}

const useVaultsCount = (values: Input): Output => {
  const { filter, blacklistTotal } = values

  const { sdk, address } = useConfig()

  const { data, isFetching } = useVaultsCountQuery({
    urls: sdk.config.api,
    variables: {
      address: address || ZeroAddress,
    },
  })

  const filterTabs = useMemo(() => (
    filterOptions.map(({ title, value }) => {
      if (value === filterTypes.all) {
        return {
          id: value,
          title,
        }
      }

      let counter = 0

      if (value === filterTypes.deposits) {
        counter = data?.deposits?.length || 0
      }

      return {
        title,
        counter,
        id: value,
        isCounterFetching: isFetching,
      }
    })
  ), [ data, isFetching ])

  const total = useMemo(() => {
    const isAll = filter === filterTypes.all

    const count = isAll
      ? data?.[filter]?.vaultsTotal
      : data?.[filter]?.length

    const result = isAll && count
      ? Math.max(count - blacklistTotal, 0)
      : count

    return result || 0
  }, [ data, filter, blacklistTotal ])

  return useMemo(() => ({
    total,
    filterTabs,
    isFetching,
  }), [ total, filterTabs, isFetching ])
}


export default useVaultsCount
